import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { styled } from "~/ui/style/stitches.config";
import { HoloCard } from "./HoloCard";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";
import { isIOSNativeApp } from "~/utils/deviceDetection";

interface ShowcaseCardModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** The image URL for the card */
  imageUrl: string;
  /** Whether the card is reversed (upside down) */
  reversed?: boolean;
  /** Layout ID for shared element transition with framer-motion */
  layoutId?: string;
  /** Called when the modal should close */
  onClose: () => void;
  /** Optional card name to display */
  cardName?: string;
  /** Optional card meaning to display */
  cardMeaning?: string;
  /** Optional background color for the overlay */
  backgroundColor?: string;
}

/**
 * A modal that showcases a single tarot card with a 3D tilt effect.
 * Supports shared element transitions via framer-motion layoutId.
 * Optionally displays card name and meaning below the card.
 */
export const ShowcaseCardModal = ({
  isOpen,
  imageUrl,
  reversed = false,
  layoutId,
  onClose,
  cardName,
  cardMeaning,
  backgroundColor = "#f6f6f6",
}: ShowcaseCardModalProps) => {
  const { isOnMobile, appDarkMode } = useSnapshot(localState);
  const hasCardInfo = cardName || cardMeaning;



  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          key={layoutId}
          as={motion.div}
          initial={{ opacity: 0, pointerEvents: "none" as const }}
          animate={{ opacity: 1, pointerEvents: "auto" as const }}
          exit={{ opacity: 0, pointerEvents: "none" as const }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          style={{ backgroundColor }}
        >
          {/* Mobile close button */}
          {isOnMobile && (
            <motion.img
              className="x-button"
              src="/images/exit.svg"
              alt="close"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, delay: 0.15 }}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              style={{
                position: "fixed",
                top: isIOSNativeApp() ? "20px" : "70px",
                right: "16px",
                width: "28px",
                height: "28px",
                cursor: "pointer",
                opacity: 0.8,
                zIndex: 1001,
                filter: appDarkMode ? "invert(1)" : undefined,
              }}
            />
          )}
          <ContentWrapper onClick={onClose}>
            <motion.div
              layoutId={layoutId}
              transition={{
                layout: {
                  type: "spring",
                  stiffness: 200,
                  damping: 25,
                },
              }}
              style={{
                width: 280,
                height: 453,
                filter: "drop-shadow(0 7px 10px rgba(0,0,0,0.3))",
              }}
            >
              <HoloCard width={280} height={453} restingRotateX={5} borderRadius={12}>
                <CardImage
                  reversed={reversed}
                  style={{ backgroundImage: `url(${imageUrl})` }}
                />
              </HoloCard>
            </motion.div>
            {hasCardInfo && (
              <CardInfo
                as={motion.div}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.3, delay: 0.15 }}
              >
                {cardName && (
                  <CardName>
                    {cardName}
                    {reversed && <ReversedLabel> (Reversed)</ReversedLabel>}
                  </CardName>
                )}
                {cardMeaning && <CardMeaning>{cardMeaning}</CardMeaning>}
              </CardInfo>
            )}
          </ContentWrapper>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

const Overlay = styled("div", {
  position: "fixed",
  inset: 0,
  backgroundColor: "#f6f6f6",
  display: "flex",
  justifyContent: "center",
  zIndex: 1000,
  overflowY: "auto",
  overscrollBehavior: "contain",
  padding: "60px 20px",
});

const ContentWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "320px",
  margin: "auto",
});

const CardImage = styled("div", {
  width: "100%",
  height: "100%",
  backgroundSize: "cover",
  backgroundPosition: "center",
  borderRadius: "12px",
  border: "1px solid rgba(0, 0, 0, 0.5)",

  variants: {
    reversed: {
      true: {
        transform: "rotate(180deg)",
      },
      false: {},
    },
  },
});

const CardInfo = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "6px",
  textAlign: "center",
  color: "$text",
  marginTop: "40px",
  width: "100%",
});

const CardName = styled("div", {
  fontFamily: "KeplerLightCondensed",
  fontSize: "28px",
});

const ReversedLabel = styled("span", {
  opacity: 0.8,
});

const CardMeaning = styled("div", {
  fontSize: "14px",
  lineHeight: "1.4",
  opacity: 0.9,
});

const ReversedNote = styled("div", {
  fontSize: "11px",
  lineHeight: "1.5",
  opacity: ".7",
  marginTop: "6px",
});
